import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import api from '../../lib/api';
import { useCryptoStore } from '../../stores/cryptoStore';
import { bootstrapNewChannelKey } from '../../lib/newChannelKey';

interface Props {
  orgId: string;
  open: boolean;
  onClose: () => void;
}

export default function CreateChannelModal({ orgId, open, onClose }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [type] = useState<'PRIVATE'>('PRIVATE');
  const [topic, setTopic] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/api/v1/orgs/${orgId}/channels`, {
        name,
        type,
        topic: topic || undefined,
      });
      const channel = data.data as { id: string };

      // Epoch 1 is created by the server in DISTRIBUTING state; finish it so the
      // creator's key IS epoch 1's key (no separate flat key, no legacy /keys POST).
      const secretKey = useCryptoStore.getState().secretKey;
      const outcome = secretKey ? await bootstrapNewChannelKey(channel.id, secretKey) : null;
      if (outcome?.outcome !== 'ready') {
        console.warn('[E2EE] Initial channel key not distributed yet.', outcome);
        toast.error(
          'Group created, but encryption is still being set up. You can send once it finishes; reopen the group if it does not.',
        );
      }

      return channel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', orgId] });
      setName('');
      setTopic('');
      setError('');
      onClose();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      setError(axiosErr.response?.data?.error?.message || 'Failed to create group');
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Create Group">
      {error && <div className="mb-3 rounded bg-red-50 p-2 text-sm text-red-600">{error}</div>}
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="e.g. Morning Team"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Topic (optional)</label>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="w-full rounded border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="What's this group about?"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || mutation.isPending}
            className="rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
